// components/MessageList.tsx
import React, { useRef, useEffect } from 'react';
import { Message } from './ChatInterface';
import { Theme } from '../types/theme';

import MarkdownRenderer from './MarkdownRenderer';
import { chatService } from '../api/chatService';
import { ViewMode } from '../types';
import FileUpload from './chat_components/FileUpload';
import MessageActions from './MessageActions';
import ReviewOverlay from './ReviewOverlay';

interface MessageListProps {
  messages: Message[];
  theme: Theme;
  viewMode: ViewMode;
  agentName?: string;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  theme,
  viewMode,
  agentName
}) => {
  const activeAudioRef = useRef<{ audio: HTMLAudioElement, url: string, cleanup: () => void } | null>(null);

  // Cleanup audio on unmount
  const [playingState, setPlayingState] = React.useState<{ messageId: string; state: 'loading' | 'playing' } | null>(null);


  // Feedback state
  const [feedbackMap, setFeedbackMap] = React.useState<Record<string, 'liked' | 'disliked'>>({});
  const [reviewingMessageId, setReviewingMessageId] = React.useState<string | null>(null);

  const handleLike = (messageId: string) => {
    setFeedbackMap(prev => ({
      ...prev,
      [messageId]: prev[messageId] === 'liked' ? undefined : 'liked'
    } as any));
  };

  const handleDislike = (messageId: string) => {
    // Open inline form for dislike
    setReviewingMessageId(prev => prev === messageId ? null : messageId);
  };

  const handleReviewSubmit = (messageId: string, data: { categories: string[]; comment: string }) => {
    console.log('Feedback submitted:', { messageId, ...data });
    setFeedbackMap(prev => ({
      ...prev,
      [messageId]: 'disliked'
    }));
    setReviewingMessageId(null);
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (activeAudioRef.current) {
        activeAudioRef.current.cleanup();
      }
    };
  }, []);

  const handleAudioAction = (message: Message, action: 'start' | 'stop', callbacks?: { onPlayStart: () => void; onEnd: () => void }) => {
    let cancelled = false;
    if (action === 'stop') {
      if (activeAudioRef.current) {
        activeAudioRef.current.cleanup();
      }
      setPlayingState(null);
      callbacks?.onEnd(); // Ensure callback is called if provided
      return;
    }

    // action === 'start'
    // Stop any existing audio first
    if (activeAudioRef.current) {
      activeAudioRef.current.cleanup();
    }

    setPlayingState({ messageId: message.id, state: 'loading' });

    const mediaSource = new MediaSource();
    const url = URL.createObjectURL(mediaSource);
    const audio = new Audio(url);

    const cleanup = () => {
      cancelled = true;

      try {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
        URL.revokeObjectURL(url);
      } catch { }

      activeAudioRef.current = null;
      setPlayingState(null);
      callbacks?.onEnd();
    };


    activeAudioRef.current = { audio, url, cleanup };

    mediaSource.addEventListener('sourceopen', async () => {
      try {
        const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
        sourceBuffer.mode = 'sequence';

        // Signal start immediately
        setPlayingState({ messageId: message.id, state: 'playing' });
        callbacks?.onPlayStart();

        const stream = await chatService.streamTextToSpeech(message.content!, 'Mason-PlayAI', 'mp3');
        const reader = stream.getReader();

        const pump = async () => {
          try {
            // Check if cancelled or sourceBuffer is removed
            if (cancelled || !mediaSource || mediaSource.readyState === 'closed') {
              reader.cancel().catch(() => { });
              return;
            }

            const { done, value } = await reader.read();
            if (done) {
              if (mediaSource.readyState === 'open') {
                mediaSource.endOfStream();
              }
              return;
            }

            if (value && mediaSource.readyState === 'open' && sourceBuffer.updating === false) {
              try {
                sourceBuffer.appendBuffer(value as BufferSource);
                await new Promise<void>((resolve) => {
                  const onUpdateEnd = () => {
                    sourceBuffer.removeEventListener('updateend', onUpdateEnd);
                    resolve();
                  };
                  sourceBuffer.addEventListener('updateend', onUpdateEnd, { once: true });
                });

                if (audio && audio.paused) {
                  audio.play().catch(() => { });
                }
              } catch (appendErr) {
                console.error('Error appending buffer:', appendErr);
                throw appendErr;
              }
            }
            await pump();
          } catch (err) {
            console.error('Error reading stream:', err);
            try {
              if (mediaSource.readyState === 'open') {
                mediaSource.endOfStream();
              }
            } catch { }
            reader.cancel().catch(() => { });
          }
        };

        await pump();

      } catch (err) {
        console.error('MediaSource/Streaming error:', err);
        cleanup();
      }
    });

    audio.onended = cleanup;
    audio.onerror = () => {
      console.error('Audio playback error', audio?.error);
      cleanup();
    };
  };

  // Stop audio when new messages are added (e.g. user sends a message)
  useEffect(() => {
    // We only want to stop if the length changes (new message), not necessarily on every update if it's just streaming
    // But since we are inside the component, we can just check if we are playing.
    // If the user sends a message, or a new response starts, we stop reading previous ones.
    if (activeAudioRef.current && playingState) {
      // Optional: logic to decide if we really want to stop.
      // The user said "sending message... stop reading".
      // If we blindly stop on any message length change, then when the user Sends, it stops. Good.
      // When bot starts replying, it stops. Good.
      activeAudioRef.current.cleanup();
      setPlayingState(null);
    }
  }, [messages.length]);
  if (messages.length === 0) return null;

  return (
    <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
      {messages.map((message) => {
        const isUser = message.type === 'user';
        const isAssistant = message.type === 'assistant';
        return (
          <div
            key={message.id}
            className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'
              } items-start group`}
          >
            <div
              className={`flex flex-col ${isUser ? 'items-end' : 'items-start'
                } ${isUser
                  ? 'max-w-[70%]'
                  : viewMode === 'widget'
                    ? 'flex-1'
                    : 'max-w-[75%]'
                }`}
            >
              <div
                className={`flex items-center gap-2 mb-1 px-1 ${isUser ? 'flex-row-reverse' : 'flex-row'
                  }`}
              >
              </div>
              {(message.content || message.error) && (
                <div
                  className={`rounded-2xl shadow-sm transition-all duration-200 hover:shadow-md ${isUser ? 'rounded-tr-md' : 'rounded-tl-md'
                    }`}
                  style={{
                    backgroundColor: isUser
                      ? theme.primaryColor
                      : theme.surfaceColor,
                    color: isUser ? '#ffffff' : theme.textColor,
                    padding: '8px 12px',

                    border: isAssistant
                      ? `1px solid ${theme.borderColor}`
                      : 'none',
                  }}
                >
                  {/* File Upload */}
                  {message.files && message.files.length > 0 && (
                    <div className="mb-3">
                      <FileUpload
                        files={message.files}
                        onRemove={() => { }}
                        theme={theme}
                      />
                    </div>
                  )}

                  {/* Text Content */}
                  <div className="text-sm leading-relaxed font-medium prose prose-sm prose-p:m-0">
                    {isAssistant ? (
                      <div className="prose-p:m-0 prose-ul:m-0 prose-li:m-0">
                        <MarkdownRenderer content={message.content} theme={theme} />
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap">{message.content}</div>
                    )}
                  </div>

                  {/* Error */}
                  {message.error && (
                    <div className="px-4 pb-3">
                      <div
                        className="text-xs mt-2 px-3 py-2 rounded-lg"
                        style={{
                          color: '#ef4444',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        }}
                      >
                        ⚠ {message.error}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 🔄 Loading dots - MOVED OUTSIDE THE BUBBLE */}
              {message.isLoading && isAssistant && (
                <div className="flex items-center space-x-2 px-1 py-1 mt-2">
                  <div className="flex space-x-1.5">
                    {[0, 150, 300].map((delay, i) => (
                      <div
                        key={i}
                        className="w-2 h-2 rounded-full animate-bounce"
                        style={{
                          backgroundColor: theme.primaryColor,
                          animationDelay: `${delay}ms`,
                          opacity: 0.6,
                        }}
                      />
                    ))}
                  </div>
                  <span
                    className="text-xs font-medium"
                    style={{ color: theme.textSecondaryColor }}
                  >
                    Thinking...
                  </span>
                </div>
              )}

              {/* Custom Component (buttons, etc.) */}
              {message.component && (
                <div className="mt-3 w-full">{message.component}</div>
              )}

              {/* Audio & Feedback Controls */}
              {isAssistant &&
                message.content &&
                !message.isLoading &&
                !message.isTyping &&
                !message.error && (
                  <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <MessageActions
                      theme={theme}
                      audioProps={{
                        theme,
                        textToSpeak: message.content,
                        externalState: playingState?.messageId === message.id ? playingState.state : 'idle',
                        onAudioAction: (action, callbacks) => handleAudioAction(message, action, callbacks)
                      }}
                      onLike={() => handleLike(message.id)}
                      onDislike={() => handleDislike(message.id)}
                      feedbackStatus={feedbackMap[message.id] || 'none'}
                    />
                  </div>
                )}


            </div>
          </div>
        );
      })}


      <ReviewOverlay
        isOpen={!!reviewingMessageId}
        onSubmit={(data) => {
          if (reviewingMessageId) handleReviewSubmit(reviewingMessageId, data);
        }}
        onCancel={() => setReviewingMessageId(null)}
        theme={theme}
      />
    </div>
  );
};

export default MessageList;